/**
 * Before/after comparison.
 *
 * A native range input is stretched over the image, so keyboard, screen
 * readers and the form semantics come for free. Dragging is handled here
 * rather than left to the input: a range only moves when the gesture starts on
 * its thumb, and a thumb wide enough to grab on a phone is also wide enough to
 * drift from the handle drawn at that percentage. The thumb is a hairline; the
 * whole frame is the drag surface.
 */
if (!customElements.get('before-after')) {
  customElements.define(
    'before-after',
    class BeforeAfter extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('.before-after__range');
        if (!this.range) return;

        this.sync = this.sync.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);

        this.range.addEventListener('input', this.sync);
        this.range.addEventListener('pointerdown', this.onPointerDown);
        this.range.addEventListener('pointermove', this.onPointerMove);
        this.range.addEventListener('pointerup', this.onPointerUp);
        this.range.addEventListener('pointercancel', this.onPointerUp);
        this.sync();
      }

      disconnectedCallback() {
        if (!this.range) return;
        this.range.removeEventListener('input', this.sync);
        this.range.removeEventListener('pointerdown', this.onPointerDown);
        this.range.removeEventListener('pointermove', this.onPointerMove);
        this.range.removeEventListener('pointerup', this.onPointerUp);
        this.range.removeEventListener('pointercancel', this.onPointerUp);
      }

      get vertical() {
        return this.classList.contains('before-after--vertical');
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.range.setPointerCapture(event.pointerId);
        this.moveTo(event);
      }

      onPointerMove(event) {
        if (this.dragging) this.moveTo(event);
      }

      onPointerUp(event) {
        this.dragging = false;
        if (this.range.hasPointerCapture(event.pointerId)) {
          this.range.releasePointerCapture(event.pointerId);
        }
      }

      moveTo(event) {
        const box = this.range.getBoundingClientRect();
        const along = this.vertical
          ? (event.clientY - box.top) / box.height
          : (event.clientX - box.left) / box.width;
        const value = Math.round(Math.min(1, Math.max(0, along)) * 100);
        if (String(value) === this.range.value) return;
        this.range.value = value;
        this.sync();
      }

      sync() {
        const value = Number(this.range.value);
        this.style.setProperty('--ba-pos', `${value}%`);
        // The label is the position, not a bare number, for anyone listening
        this.range.setAttribute('aria-valuetext', `${value}%`);
      }
    }
  );
}
